import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { Box } from 'components/Box';
import { Flex } from 'components/Flex';
import Spacer from 'components/Spacer';
import { Link } from 'react-router-dom';
import { useMeasure } from 'context/measure';
import { Observation } from 'types/api/envScales/Observation';
import { Button, Icon } from 'semantic-ui-react';
import ClassroomExample from './ClassroomExample';
import VisuallyHidden from 'components/VisuallyHidden';

import {
  Section,
  SectionHeader,
  SectionContent
} from 'components/Measure/View/Styled';

import {
  ThumbsUp,
  TrendingUp
} from '@teachstonellc/component-library/dist/icons';

const ICON_SIZE = 20;

interface ClassroomExamplesProps {
  /** If `true`, the edit button will be displayed. */
  showEdit?: boolean;

  /**
   * Observation to render. Pages that refetch the assessment themselves should
   * pass it, because the measure context is loaded once per provider mount and
   * is stale after the observation was edited from another route.
   */
  observation?: Observation;
}

function ClassroomExamples({
  showEdit,
  observation: observationProp
}: ClassroomExamplesProps) {
  const { assessment } = useMeasure();
  const observation = observationProp ?? assessment.observation!;
  const editLink = RouteHelpers.getPath('measure-examples-notes', {
    id: assessment.id
  });

  return (
    <Section className="mt-2">
      <Flex justify="space-between" align="center">
        <SectionHeader>{i18n.ft(messages.title)}</SectionHeader>

        {showEdit && (
          <Box mr={{ _: '0px', sm: '56px' }}>
            <Button icon color="blue" size="mini" as={Link} to={editLink}>
              <VisuallyHidden>{i18n.ft(messages.editFeedback)}</VisuallyHidden>
              <Icon fitted name="pencil" />
            </Button>
          </Box>
        )}
      </Flex>

      <SectionContent>
        <ClassroomExample
          title={i18n.ft(messages.strengthExample)}
          icon={<ThumbsUp size={ICON_SIZE} className="text-scale-blue" />}
          notes={observation.strength_text}
          dimensionId={observation.selected_strength_dimension_id}
          indicatorId={observation.selected_strength_indicator_id}
          suggestedDimensionId={observation.recommended_strength_dimension_id}
          suggestedIndicatorId={observation.recommended_strength_indicator_id}
        />
        <Spacer size={32} />
        <ClassroomExample
          title={i18n.ft(messages.growthExample)}
          icon={<TrendingUp size={ICON_SIZE} className="text-scale-blue" />}
          notes={observation.improve_text}
          dimensionId={observation.selected_improve_dimension_id}
          indicatorId={observation.selected_improve_indicator_id}
          suggestedDimensionId={observation.recommended_improve_dimension_id}
          suggestedIndicatorId={observation.recommended_improve_indicator_id}
        />
      </SectionContent>
    </Section>
  );
}

export default ClassroomExamples;
